import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import Swal from 'sweetalert2';
import { Howl } from 'howler';
import NotificacionesFinanzasContext from './NotificacionesFinanzasContext';
import UserContext from '../User/UserContext';
import { initWebsockets } from '../../libs/http/initWebsocket';
import {
  obtenerTransaccionesPendientes,
  confirmarEntrega,
  rechazarEntrega,
  confirmarTraslado,
  rechazarTraslado
} from '../../api/finanzas';
import { Alertassw } from '../../components/Alertas/Alertassw';
import { extractError } from '../../components/Finanzas/utils/extractError';
import ModalNotificacionTransaccion from '../../components/Finanzas/ModalNotificacionTransaccion';
import sonidoNotificacion from '../../sound/Cash-register-fake.mp3';

const normalizarConTipo = (payload, tipo) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.tipo) return payload;
  return { ...payload, tipo };
};

const NotificacionesFinanzasState = ({ children }) => {
  const userCtx = useContext(UserContext);
  const userData = userCtx && userCtx.userData;

  const [cola, setCola] = useState([]);
  const [procesando, setProcesando] = useState(false);
  const [bolsilloUpdate, setBolsilloUpdate] = useState(null);
  // Cuando rechazar() abre un SweetAlert encima del Modal de Bootstrap, el
  // focus trap del Modal impide escribir en el input del Swal. Mientras
  // `modalSuprimido` está en true, el modal de Bootstrap no se renderiza.
  const [modalSuprimido, setModalSuprimido] = useState(false);

  const ultimoIdReproducidoRef = useRef(null);
  const sonidoPendienteRef = useRef(false);
  const audioDesbloqueadoRef = useRef(false);
  // Howl precargado para que el primer play() no espere a descargar el mp3.
  const howlRef = useRef(null);
  if (!howlRef.current) {
    try {
      howlRef.current = new Howl({
        src: [sonidoNotificacion],
        volume: 0.8,
        preload: true,
        html5: false
      });
    } catch (err) {
      console.warn('[notif finanzas] no se pudo precargar Howl:', err);
    }
  }

  const reproducirSonido = useCallback(() => {
    const howl = howlRef.current;
    if (!howl) return;
    try {
      // Detener cualquier reproducción previa del mismo Howl para reiniciar.
      howl.stop();
      const id = howl.play();
      howl.once('playerror', () => {
        sonidoPendienteRef.current = true;
      });
      if (!id) sonidoPendienteRef.current = true;
    } catch (err) {
      console.warn('[notif finanzas] error reproduciendo sonido:', err);
      sonidoPendienteRef.current = true;
    }
  }, []);

  // Desbloqueo de audio: el navegador bloquea autoplay en hard refresh hasta
  // que haya un gesto del usuario. Al primer click/keydown/touchstart
  // reproducimos cualquier sonido pendiente.
  useEffect(() => {
    if (audioDesbloqueadoRef.current) return;

    const desbloquear = () => {
      if (audioDesbloqueadoRef.current) return;
      audioDesbloqueadoRef.current = true;
      if (sonidoPendienteRef.current) {
        sonidoPendienteRef.current = false;
        reproducirSonido();
      }
      window.removeEventListener('click', desbloquear);
      window.removeEventListener('keydown', desbloquear);
      window.removeEventListener('touchstart', desbloquear);
    };

    window.addEventListener('click', desbloquear);
    window.addEventListener('keydown', desbloquear);
    window.addEventListener('touchstart', desbloquear);

    return () => {
      window.removeEventListener('click', desbloquear);
      window.removeEventListener('keydown', desbloquear);
      window.removeEventListener('touchstart', desbloquear);
    };
  }, [reproducirSonido]);

  const encolarSiNoExiste = useCallback((nueva) => {
    if (!nueva || !nueva._id || !nueva.tipo) return;
    setCola((prev) => {
      const yaEnCola = prev.some(
        (t) => t.tipo === nueva.tipo && String(t._id) === String(nueva._id)
      );
      if (yaEnCola) return prev;
      return [...prev, nueva];
    });
  }, []);

  const removerDeCola = useCallback((tipo, id) => {
    if (!tipo || !id) return;
    setCola((prev) => prev.filter(
      (t) => !(t.tipo === tipo && String(t._id) === String(id))
    ));
  }, []);

  const marcarBolsilloActualizado = useCallback((info) => {
    setBolsilloUpdate({ recibidoEn: Date.now(), ...info });
  }, []);

  // Fetch inicial de pendientes cuando hay sesión
  useEffect(() => {
    if (!userData || !userData._id) return;

    let cancelado = false;
    const fetchPendientes = async () => {
      try {
        const resp = await obtenerTransaccionesPendientes();
        const pendientes = (resp && resp.data && resp.data.data) || [];
        if (cancelado) return;
        pendientes.forEach((t) => encolarSiNoExiste(t));
      } catch (err) {
        console.warn('[notif finanzas] no se pudieron traer pendientes:', err);
      }
    };
    fetchPendientes();
    return () => { cancelado = true; };
  }, [userData, encolarSiNoExiste]);

  // Suscripción Socket.io
  useEffect(() => {
    if (!userData || !userData._id) return;
    const socket = initWebsockets();
    if (!socket) return;

    const onEntregaRecibida = (payload) =>
      encolarSiNoExiste(normalizarConTipo(payload, 'ENTREGA'));
    const onTrasladoRecibido = (payload) =>
      encolarSiNoExiste(normalizarConTipo(payload, 'TRASLADO'));

    const onEntregaConfirmada = (payload) => {
      removerDeCola('ENTREGA', payload && payload._id);
      marcarBolsilloActualizado({ evento: 'ENTREGA_CONFIRMADA', payload });
    };
    const onEntregaRechazada = (payload) => {
      removerDeCola('ENTREGA', payload && payload._id);
      marcarBolsilloActualizado({ evento: 'ENTREGA_RECHAZADA', payload });
    };
    const onTrasladoConfirmado = (payload) => {
      removerDeCola('TRASLADO', payload && payload._id);
      marcarBolsilloActualizado({ evento: 'TRASLADO_CONFIRMADO', payload });
    };
    const onTrasladoRechazado = (payload) => {
      removerDeCola('TRASLADO', payload && payload._id);
      marcarBolsilloActualizado({ evento: 'TRASLADO_RECHAZADO', payload });
    };

    const onBolsilloActualizado = (payload) =>
      marcarBolsilloActualizado({ evento: 'BOLSILLO_ACTUALIZADO', payload });
    const onBolsilloConDeuda = (payload) =>
      marcarBolsilloActualizado({ evento: 'BOLSILLO_CON_DEUDA', payload });

    socket.on('ENTREGA_RECIBIDA', onEntregaRecibida);
    socket.on('TRASLADO_RECIBIDO', onTrasladoRecibido);
    socket.on('ENTREGA_CONFIRMADA', onEntregaConfirmada);
    socket.on('ENTREGA_RECHAZADA', onEntregaRechazada);
    socket.on('TRASLADO_CONFIRMADO', onTrasladoConfirmado);
    socket.on('TRASLADO_RECHAZADO', onTrasladoRechazado);
    socket.on('BOLSILLO_ACTUALIZADO', onBolsilloActualizado);
    socket.on('BOLSILLO_CON_DEUDA', onBolsilloConDeuda);

    return () => {
      socket.off('ENTREGA_RECIBIDA', onEntregaRecibida);
      socket.off('TRASLADO_RECIBIDO', onTrasladoRecibido);
      socket.off('ENTREGA_CONFIRMADA', onEntregaConfirmada);
      socket.off('ENTREGA_RECHAZADA', onEntregaRechazada);
      socket.off('TRASLADO_CONFIRMADO', onTrasladoConfirmado);
      socket.off('TRASLADO_RECHAZADO', onTrasladoRechazado);
      socket.off('BOLSILLO_ACTUALIZADO', onBolsilloActualizado);
      socket.off('BOLSILLO_CON_DEUDA', onBolsilloConDeuda);
    };
  }, [userData, encolarSiNoExiste, removerDeCola, marcarBolsilloActualizado]);

  const recibir = useCallback(async (transaccion) => {
    if (!transaccion) return;
    setProcesando(true);
    try {
      const llamada = transaccion.tipo === 'ENTREGA'
        ? confirmarEntrega(transaccion._id, {})
        : confirmarTraslado(transaccion._id, {});
      const resp = await llamada;
      if (resp && resp.status === 200) {
        removerDeCola(transaccion.tipo, transaccion._id);
        marcarBolsilloActualizado({
          evento: 'TRANSACCION_RECIBIDA',
          payload: { tipo: transaccion.tipo, _id: transaccion._id }
        });
        Alertassw('toast', 'Recibido', 'Transacción confirmada.', 'success');
      } else {
        Alertassw('error', 'Error', 'No se pudo confirmar la transacción.');
      }
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 409) {
        await Swal.fire({
          icon: 'info',
          title: 'Transacción ya procesada',
          text: 'Esta transacción ya fue procesada en otra sesión.'
        });
        removerDeCola(transaccion.tipo, transaccion._id);
      } else {
        Alertassw('error', 'Error', extractError(err));
      }
    } finally {
      setProcesando(false);
    }
  }, [removerDeCola, marcarBolsilloActualizado]);

  const rechazar = useCallback(async (transaccion) => {
    if (!transaccion) return;
    // Ocultar el modal de Bootstrap mientras el Swal pide motivo, para que el
    // focus trap del Modal no bloquee el input del Swal.
    setModalSuprimido(true);
    let motivo;
    let isConfirmed = false;
    try {
      const result = await Swal.fire({
        title: '¿Rechazar transacción?',
        input: 'textarea',
        inputLabel: 'Motivo del rechazo',
        inputPlaceholder: 'Escribe el motivo...',
        inputValidator: (val) => (!val ? 'Debes indicar un motivo' : undefined),
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Rechazar',
        cancelButtonText: 'Cancelar',
        heightAuto: false
      });
      motivo = result.value;
      isConfirmed = result.isConfirmed;
    } finally {
      setModalSuprimido(false);
    }

    if (!isConfirmed || !motivo) return;

    setProcesando(true);
    try {
      const llamada = transaccion.tipo === 'ENTREGA'
        ? rechazarEntrega(transaccion._id, motivo)
        : rechazarTraslado(transaccion._id, motivo);
      const resp = await llamada;
      if (resp && resp.status === 200) {
        removerDeCola(transaccion.tipo, transaccion._id);
        marcarBolsilloActualizado({
          evento: 'TRANSACCION_RECHAZADA',
          payload: { tipo: transaccion.tipo, _id: transaccion._id }
        });
        Alertassw('toast', 'Rechazada', 'Transacción rechazada.', 'success');
      } else {
        Alertassw('error', 'Error', 'No se pudo rechazar la transacción.');
      }
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 409) {
        await Swal.fire({
          icon: 'info',
          title: 'Transacción ya procesada',
          text: 'Esta transacción ya fue procesada en otra sesión.'
        });
        removerDeCola(transaccion.tipo, transaccion._id);
      } else {
        Alertassw('error', 'Error', extractError(err));
      }
    } finally {
      setProcesando(false);
    }
  }, [removerDeCola, marcarBolsilloActualizado]);

  // Difiere la decisión: solo saca de la cola en el cliente, sin tocar el backend.
  const cerrar = useCallback(() => {
    setCola((prev) => prev.slice(1));
  }, []);

  const transaccionActual = cola.length > 0 ? cola[0] : null;

  // Reproducir sonido cada vez que el modal cambia a mostrar una transacción
  // distinta (o pasa de oculto a visible).
  useEffect(() => {
    const idActual = transaccionActual ? String(transaccionActual._id) : null;
    if (idActual && idActual !== ultimoIdReproducidoRef.current) {
      ultimoIdReproducidoRef.current = idActual;
      reproducirSonido();
    }
    if (!idActual) {
      ultimoIdReproducidoRef.current = null;
    }
  }, [transaccionActual, reproducirSonido]);

  const value = {
    cola,
    transaccionActual,
    pendientesCount: cola.length,
    procesando,
    bolsilloUpdate,
    recibir,
    rechazar,
    cerrar
  };

  return (
    <NotificacionesFinanzasContext.Provider value={value}>
      {children}
      {transaccionActual && !modalSuprimido && (
        <ModalNotificacionTransaccion
          transaccion={transaccionActual}
          procesando={procesando}
          onRecibir={() => recibir(transaccionActual)}
          onRechazar={() => rechazar(transaccionActual)}
          onCerrar={cerrar}
        />
      )}
    </NotificacionesFinanzasContext.Provider>
  );
};

export default NotificacionesFinanzasState;
